import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import visualService from '../../services/visualService';

/**
 * ArtifactShellRenderer loads shell.html into an iframe and handles:
 * - Page navigation via hash routing
 * - postMessage communication with the shell
 * - Fallback to legacy full-HTML-per-page rendering
 *
 * Security: The iframe uses sandbox="allow-scripts allow-same-origin".
 * Page content is loaded exclusively from our backend API (not user input).
 */
const ArtifactShellRenderer = ({
  projectId,
  taskType = 'wireframes',
  currentPageId = null,
  contentKey = 0,
  onPageChange = null,
  legacyHtmlContent = null,
  legacyContentUrl = null,
}) => {
  const iframeRef = useRef(null);
  const [shellHtml, setShellHtml] = useState(null);
  const [hasShell, setHasShell] = useState(null); // null = loading, true/false = resolved
  const [loading, setLoading] = useState(true);

  // Check for shell.html on mount — falls back to legacy if not found or on error
  useEffect(() => {
    const loadShell = async () => {
      if (!projectId) {
        setHasShell(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const result = await visualService.getShellContent(projectId, taskType);
        if (result?.data?.has_shell && result?.data?.html_content) {
          setShellHtml(result.data.html_content);
          setHasShell(true);
        } else {
          // No shell found — fall through to legacy rendering (Tier 2/3)
          console.log('ArtifactShellRenderer: No shell.html found, using legacy rendering');
          setHasShell(false);
        }
      } catch (err) {
        // API error — fall through to legacy rendering gracefully
        console.warn('ArtifactShellRenderer: Shell fetch failed, falling back to legacy:', err);
        setHasShell(false);
      } finally {
        setLoading(false);
      }
    };
    loadShell();
  }, [projectId, taskType, contentKey]);

  // Navigate to a specific page in the shell
  useEffect(() => {
    if (hasShell && currentPageId && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'navigate', pageId: currentPageId },
        '*'
      );
    }
  }, [currentPageId, hasShell]);

  // Listen for navigation events from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'page-changed' && onPageChange) {
        onPageChange(event.data.pageId);
      }
      // Respond to token requests from iframe
      if (event.data?.type === 'request-token' && iframeRef.current?.contentWindow) {
        const token = localStorage.getItem('token') || '';
        iframeRef.current.contentWindow.postMessage(
          { type: 'auth-token', token },
          '*'
        );
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onPageChange]);

  // Rewrite shell HTML to use backend API for page fetches
  const getRewrittenShellHtml = useCallback(() => {
    if (!shellHtml) return '';
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8003/api').replace(/\/api\/?$/, '');
    const pagesBase = `${backendBase}/api/visual-pages/projects/${projectId}/${taskType}/pages`;

    // Inject fetch rewriting and postMessage bridge
    // Token is retrieved via postMessage (not embedded in HTML for security)
    const injection = `
    <script>
      // Token retrieval via postMessage — must resolve BEFORE first fetch
      var _authToken = '';
      var _tokenReady = false;
      var _pendingFetches = [];

      // Request token from parent
      window.parent.postMessage({ type: 'request-token' }, '*');

      // Listen for token response and navigation commands
      window.addEventListener('message', function(e) {
        if (!e.data) return;
        if (e.data.type === 'auth-token') {
          _authToken = e.data.token || '';
          _tokenReady = true;
          // Flush any pending fetches that were waiting for the token
          _pendingFetches.forEach(function(pf) { pf(); });
          _pendingFetches = [];
        }
        if (e.data.type === 'navigate' && typeof router !== 'undefined') {
          router.navigate(e.data.pageId);
        }
      });

      // Rewrite fetch — queues page fetches until token is ready
      var _originalFetch = window.fetch;
      window.fetch = function(url, opts) {
        if (typeof url === 'string' && url.endsWith('.html') && !url.startsWith('http')) {
          var pageName = url.replace('.html', '');
          var apiUrl = '${pagesBase}/' + pageName;

          var doFetch = function() {
            var headers = _authToken ? { 'Authorization': 'Bearer ' + _authToken } : {};
            return _originalFetch(apiUrl, { headers: headers })
              .then(function(r) { return r.json(); })
              .then(function(json) {
                var html = (json && json.data && json.data.html_content) || '<div class="p-8 text-gray-500">Page not found</div>';
                return new Response(html, { status: 200, headers: { 'Content-Type': 'text/html' } });
              })
              .catch(function() {
                return new Response('<div class="p-8 text-gray-500">Error loading page</div>', { status: 200, headers: { 'Content-Type': 'text/html' } });
              });
          };

          if (_tokenReady) {
            return doFetch();
          }
          // Token not ready yet — queue this fetch and return a promise that resolves when token arrives
          return new Promise(function(resolve) {
            _pendingFetches.push(function() { resolve(doFetch()); });
          });
        }
        return _originalFetch(url, opts);
      };

      // Wait for router to be defined, then patch it (with 5s timeout)
      var _patchAttempts = 0;
      var _patchInterval = setInterval(function() {
        _patchAttempts++;
        if (typeof router !== 'undefined' && router.navigate) {
          var _origNav = router.navigate.bind(router);
          router.navigate = function(pageId) {
            _origNav(pageId);
            window.parent.postMessage({ type: 'page-changed', pageId: pageId }, '*');
          };
          clearInterval(_patchInterval);
        } else if (_patchAttempts > 100) {
          clearInterval(_patchInterval);
        }
      }, 50);
    </script>`;

    // Insert injection EARLY — right after <head> so fetch is rewritten BEFORE
    // the shell's own scripts run and call router.navigate() → fetch()
    if (shellHtml.includes('</head>')) {
      return shellHtml.replace('</head>', injection + '</head>');
    }
    // Fallback: insert after opening body tag
    if (shellHtml.includes('<body')) {
      return shellHtml.replace(/<body([^>]*)>/, '<body$1>' + injection);
    }
    return injection + shellHtml;
  }, [shellHtml, projectId, taskType]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading preview...
        </Typography>
      </Box>
    );
  }

  // Shell mode: render shell.html with fetch-based page loading
  if (hasShell && shellHtml) {
    return (
      <iframe
        ref={iframeRef}
        key={`shell-${contentKey}`}
        srcDoc={getRewrittenShellHtml()}
        title={`${taskType} Preview`}
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'white',
        }}
      />
    );
  }

  // Legacy mode: full HTML page per file (backward compatible)
  if (legacyContentUrl) {
    return (
      <iframe
        key={`legacy-url-${contentKey}`}
        src={legacyContentUrl}
        title={`${taskType} Preview`}
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '600px',
          border: 'none',
          background: 'white',
        }}
      />
    );
  }

  if (legacyHtmlContent) {
    return (
      <iframe
        key={`legacy-html-${contentKey}`}
        srcDoc={legacyHtmlContent}
        title={`${taskType} Preview`}
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '600px',
          border: 'none',
          background: 'white',
        }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography color="text.secondary">No preview available</Typography>
    </Box>
  );
};

export default ArtifactShellRenderer;
